import React, { useState, useEffect } from 'react';
import axios from 'axios';
import ls from 'localstorage-slim';
import FileSaver from 'file-saver';
import * as XLSX from 'xlsx-js-style';
import { FaFileExcel } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';

import { refreshToken } from '../helpers';
import Loading from '../GeneralComponents/Loading';

function Report() {
  const [customerTotalData, setCustomerTotalData] = useState([]);
  const [customerNetTotalData, setCustomerNetTotalData] = useState([]);
  const [customerTaxData, setCustomerTaxData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const { t, i18n } = useTranslation();

  const subDomain = ls.get('subDomain');

  const fileType =
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
  const fileExtension = '.xlsx';

  const exportTotalExcelSheet = (apiData, fileName, sheetName) => {
    const header = [
      'Company',
      'January',
      'February',
      'March',
      'April',
      'May',
      'June',
      'July',
      'August',
      'September',
      'October',
      'November',
      'December',
      'Total',
    ];
    const workSheet = XLSX.utils.json_to_sheet([]);
    XLSX.utils.sheet_add_aoa(workSheet, [header], { origin: 'A1' });

    // Add cell styles to the header row
    for (let i = 0; i < header.length; i++) {
      const cellRef = XLSX.utils.encode_cell({ r: 0, c: i });
      if (!workSheet[cellRef]) workSheet[cellRef] = {};
      workSheet[cellRef].s = {
        font: { bold: true },
        fill: { fgColor: { rgb: 'D3D3D3' } },
        alignment: {
          horizontal: 'center',
        },
      };
    }

    // Reorder the keys of each object in apiData to match the header order
    const reorderedData = apiData.map(obj =>
      header.reduce((acc, key) => {
        acc[key] = obj[key] || 0;
        return acc;
      }, {})
    );

    // Calculate the total of all rows
    reorderedData.forEach(row => {
      let rowTotal = 0;
      header.forEach(columnName => {
        if (columnName !== 'Company') {
          rowTotal += row[columnName];
        }
      });
      row['Total'] = rowTotal;
    });

    // Add the 'Total' column to the worksheet
    XLSX.utils.sheet_add_json(workSheet, reorderedData, {
      origin: 'A2',
      skipHeader: true,
    });

    // Calculate the sum of all columns
    const columnSums = {};
    header.forEach(columnName => {
      if (columnName !== 'Company') {
        let columnSum = 0;
        reorderedData.forEach(row => {
          columnSum += row[columnName];
        });
        columnSums[columnName] = columnSum;
      }
    });

    const totalRow = [];
    header.forEach(columnName => {
      if (columnName === 'Company') {
        totalRow.push('Total');
      } else {
        totalRow.push(columnSums[columnName]);
      }
    });

    // Add empty row space between table and totalRow
    XLSX.utils.sheet_add_aoa(workSheet, [[]], { origin: -1 });

    XLSX.utils.sheet_add_aoa(workSheet, [totalRow], { origin: -1 });

    let range = XLSX.utils.decode_range(workSheet['!ref']);
    let num_rows = range.e.r - range.s.r;

    // Add cell styles to the 'Total' row
    for (let i = 0; i < totalRow.length - 1; i++) {
      const cellRef = XLSX.utils.encode_cell({ r: num_rows, c: i });
      if (!workSheet[cellRef]) workSheet[cellRef] = {};
      workSheet[cellRef].s = {
        font: { bold: true },
        fill: { fgColor: { rgb: 'D3D3D4' } },
        alignment: {
          horizontal: 'center',
        },
      };
    }

    // Add cell styles to the 'Total' column
    const totalColumnIndex = header.indexOf('Total');
    for (let i = 0; i < num_rows + 1; i++) {
      const cellRef = XLSX.utils.encode_cell({ r: i, c: totalColumnIndex });
      if (!workSheet[cellRef]) workSheet[cellRef] = {};
      workSheet[cellRef].s = {
        font: { bold: true },
        fill: { fgColor: { rgb: '90EE90' } },
        alignment: {
          horizontal: 'center',
        },
      };
    }

    const workBook = { Sheets: { [sheetName]: workSheet }, SheetNames: [sheetName] };
    const excelBuffer = XLSX.write(workBook, { bookType: 'xlsx', type: 'array', cellStyles: true });
    const data = new Blob([excelBuffer], { type: fileType });
    FileSaver.saveAs(data, fileName + fileExtension);
  };

  useEffect(() => {
    let taxpayer = ls.get('taxpayer_id');

    const endpoints = [
      `${subDomain}${process.env.REACT_APP_DJANGO_BASEURL}/erp/customer_total_report?taxpayer=${taxpayer}`,
      `${subDomain}${process.env.REACT_APP_DJANGO_BASEURL}/erp/customer_net_total_report?taxpayer=${taxpayer}`,
      `${subDomain}${process.env.REACT_APP_DJANGO_BASEURL}/erp/customer_tax_report?taxpayer=${taxpayer}`,
    ];

    async function fetchData() {
      try {
        if (!ls.get('access')) refreshToken();
        let [total, netTotal, tax] = await Promise.all(
          endpoints.map(endpoint => axios.get(endpoint))
        );

        setCustomerTotalData(total.data);
        setCustomerNetTotalData(netTotal.data);
        setCustomerTaxData(tax.data);
        setIsLoading(false);
      } catch (error) {
        if (error.response) {
          // Request made but the server responded with an error
          console.log(error.response.data);
          console.log(error.response.status);
          console.log(error.response.headers);
        } else if (error.request) {
          // Request made but no response is received from the server.
          console.log(error.request);
        } else {
          // Error occured while setting up the request
          console.log('Error', error.message);
        }
      }
    }

    fetchData();
  }, []);

  return isLoading ? (
    <Loading />
  ) : (
    <>
      <div className='container-fluid'>
        <div className='shadow m-3 p-2'>
          <div className={`${i18n.language === 'en' ? '' : 'text-end'} row my-2 mx-4`}>
            <div className='row mx-0 my-2'>
              <span className='d-block fs-3'>{t('report.total.title')}</span>
              <em className={i18n.language === 'en' ? '' : 'arabic'}>
                {t('report.description')}{' '}
                <span className='fw-bold text-capitalize'>{t('report.total.bold')}</span>{' '}
                {t('report.monthly')}{' '}
                <span className='text-decoration-underline fw-bold'>{t('report.sobol')}</span>.
              </em>
            </div>
            <div
              className={`${
                i18n.language === 'en' ? 'justify-content-start' : 'justify-content-end'
              } d-flex mb-2 align-items-center`}>
              <div
                className={`btn btn-success btn-sm col-2`}
                onClick={e => exportTotalExcelSheet(customerTotalData, 'Total', 'Total Amount')}
                style={{ width: '180px' }}
                role='button'>
                <FaFileExcel />
                <span className='px-2 text-nowrap'>{t('report.button')}</span>
              </div>
            </div>
            <div className='row mx-0 my-2'>
              <span className='d-block fs-3'>{t('report.netTotal.title')}</span>
              <em className={i18n.language === 'en' ? '' : 'arabic'}>
                {t('report.description')}{' '}
                <span className='fw-bold text-capitalize'>{t('report.netTotal.bold')}</span>{' '}
                {t('report.monthly')}{' '}
                <span className='text-decoration-underline fw-bold'>{t('report.sobol')}</span>.
              </em>
            </div>
            <div
              className={`${
                i18n.language === 'en' ? 'justify-content-start' : 'justify-content-end'
              } d-flex mb-2 align-items-center`}>
              <div
                className='btn btn-success btn-sm col-2'
                onClick={e => exportTotalExcelSheet(customerNetTotalData, 'Net Total', 'Net Total')}
                style={{ width: '180px' }}>
                <FaFileExcel />
                <span className='px-2 text-nowrap'>{t('report.button')}</span>
              </div>
            </div>
            <div className='row mx-0 my-2'>
              <span className='d-block fs-3'>{t('report.tax.title')}</span>
              <em className={i18n.language === 'en' ? '' : 'arabic'}>
                {t('report.description')}{' '}
                <span className='fw-bold text-capitalize'>{t('report.tax.bold')}</span>{' '}
                {t('report.monthly')}{' '}
                <span className='text-decoration-underline fw-bold'>{t('report.sobol')}</span>.
              </em>
            </div>
            <div
              className={`${
                i18n.language === 'en' ? 'justify-content-start' : 'justify-content-end'
              } d-flex mb-2 align-items-center`}>
              <div
                className='btn btn-success btn-sm col-2'
                onClick={e => exportTotalExcelSheet(customerTaxData, 'Tax', 'Tax')}
                style={{ width: '180px' }}>
                <FaFileExcel />
                <span className='px-2 text-nowrap'>{t('report.button')}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default Report;
